import { and, count, desc, eq, not } from 'drizzle-orm';

import * as schema from './db/schema';
import type { SQLocalRepo } from './sqlocal';

export * from './sqlocal';

export class Repo {
	constructor(private readonly repo: SQLocalRepo) {}

	export() {
		return this.repo.export();
	}

	import(databaseFile: File) {
		return this.repo.import(databaseFile);
	}

	purge() {
		return this.repo.purge();
	}

	async getInstance() {
		return await this.repo.db.query.instance.findFirst();
	}

	async getLastUsedChatInferenceSettings() {
		return await this.repo.db.query.inferenceSettings.findFirst({
			orderBy: desc(schema.inferenceSettings.lastUsedAt)
		});
	}

	async getDefaultPrompt() {
		return await this.repo.db.query.prompts.findFirst({
			where: and(schema.prompts.isDefault, schema.prompts.isEnabled, not(schema.prompts.isDeleted))
		});
	}

	async getDefaultChatInferenceSettings() {
		return await this.repo.db.query.inferenceSettings.findFirst({
			where: and(schema.inferenceSettings.isDefault, schema.inferenceSettings)
		});
	}

	async upsertInferenceSettings(settings: typeof schema.inferenceSettings.$inferInsert) {
		const result = await this.repo.db
			.insert(schema.inferenceSettings)
			.values({
				...settings,
				createdAt: undefined,
				updatedAt: undefined,
				lastUsedAt: undefined
			})
			.onConflictDoUpdate({
				target: schema.inferenceSettings.id,
				set: {
					...settings,
					updatedAt: new Date()
				}
			})
			.returning();
		return result.at(0);
	}

	async upsertChat(chat: typeof schema.chats.$inferInsert) {
		const result = await this.repo.db
			.insert(schema.chats)
			.values({
				...chat,
				createdAt: undefined,
				updatedAt: undefined
			})
			.onConflictDoUpdate({
				target: schema.chats.id,
				set: {
					...chat,
					updatedAt: new Date()
				}
			})
			.returning();
		return result.at(0);
	}

	async upsertAutomation(automation: typeof schema.automations.$inferInsert) {
		const result = await this.repo.db
			.insert(schema.automations)
			.values({
				...automation,
				createdAt: undefined,
				updatedAt: undefined
			})
			.onConflictDoUpdate({
				target: schema.automations.id,
				set: {
					...automation,
					updatedAt: new Date()
				}
			})
			.returning();
		return result.at(0);
	}

	async upsertChatInteraction(interaction: typeof schema.chatInteractions.$inferInsert) {
		const result = await this.repo.db
			.insert(schema.chatInteractions)
			.values({
				...interaction,
				createdAt: undefined,
				updatedAt: undefined
			})
			.onConflictDoUpdate({
				target: schema.chatInteractions.id,
				set: {
					...interaction,
					updatedAt: new Date()
				}
			})
			.returning();
		return result.at(0);
	}

	async upsertTool(fn: typeof schema.tools.$inferInsert) {
		const result = await this.repo.db
			.insert(schema.tools)
			.values({
				...fn,
				createdAt: undefined,
				updatedAt: undefined
			})
			.onConflictDoUpdate({
				target: schema.tools.id,
				set: {
					...fn,
					updatedAt: new Date()
				}
			})
			.returning();
		return result.at(0);
	}

	async upsertPrompt(prompt: typeof schema.prompts.$inferInsert) {
		const result = await this.repo.db
			.insert(schema.prompts)
			.values({
				...prompt,
				createdAt: undefined,
				updatedAt: undefined
			})
			.onConflictDoUpdate({
				target: schema.prompts.id,
				set: {
					...prompt,
					updatedAt: new Date()
				}
			})
			.returning();
		return result.at(0);
	}

	async upsertProvider(provider: typeof schema.providers.$inferInsert) {
		const result = await this.repo.db
			.insert(schema.providers)
			.values({
				...provider,
				createdAt: undefined,
				updatedAt: undefined
			})
			.onConflictDoUpdate({
				target: schema.providers.id,
				set: {
					...provider,
					updatedAt: new Date()
				}
			})
			.returning();
		return result.at(0);
	}

	async getAutomationsList({ enabledOnly } = { enabledOnly: false }) {
		return await this.repo.db.query.automations.findMany({
			where: enabledOnly ? eq(schema.automations.isEnabled, true) : undefined,
			orderBy: [desc(schema.automations.isEnabled), schema.automations.title],
			with: {
				inferenceSettings: {
					with: {
						provider: true
					}
				}
			}
		});
	}

	async getAutomation(id: string) {
		return await this.repo.db.query.automations.findFirst({
			where: eq(schema.automations.id, id),
			with: {
				inferenceSettings: true
			}
		});
	}

	async getProvidersList() {
		return await this.repo.db.query.providers.findMany({
			where: not(schema.providers.isDeleted),
			orderBy: [desc(schema.providers.isEnabled), schema.providers.title]
		});
	}

	async getChatsList({ limit }: { limit?: number } = { limit: undefined }) {
		return await this.repo.db.query.chats.findMany({
			where: eq(schema.chats.isDeleted, false),
			orderBy: [desc(schema.chats.updatedAt)],
			limit,
			with: {
				inferenceSettings: {
					with: {
						provider: true
					}
				},
				interactions: {
					where: eq(schema.chatInteractions.isDeleted, false),
					columns: { id: true }
				}
			}
		});
	}

	async getChat(id: string) {
		return await this.repo.db.query.chats.findFirst({
			where: eq(schema.chats.id, id),
			with: {
				inferenceSettings: {
					with: {
						provider: true
					}
				},
				interactions: {
					where: not(schema.chatInteractions.isDeleted),
					orderBy: schema.chatInteractions.createdAt,
					with: {
						attachments: {
							where: not(schema.chatInteractionAttachments.isDeleted),
							orderBy: schema.chatInteractionAttachments.createdAt
						}
					}
				}
			}
		});
	}

	async getProvider(id: string, { enabledOnly } = { enabledOnly: false }) {
		return await this.repo.db.query.providers.findFirst({
			where: and(
				...[
					eq(schema.providers.id, id),
					not(schema.providers.isDeleted),
					...(enabledOnly ? [eq(schema.providers.isEnabled, true)] : [])
				]
			)
		});
	}

	async getToolList({ enabledOnly } = { enabledOnly: false }) {
		return await this.repo.db.query.tools.findMany({
			where: and(
				eq(schema.tools.isDeleted, false),
				enabledOnly ? eq(schema.tools.isEnabled, true) : undefined
			)
		});
	}

	async getChatInteraction(interactionId: string) {
		return await this.repo.db.query.chatInteractions.findFirst({
			where: and(
				not(schema.chatInteractions.isDeleted),
				eq(schema.chatInteractions.id, interactionId)
			),
			with: {
				attachments: {
					orderBy: schema.chatInteractionAttachments.createdAt,
					where: and(
						not(schema.chatInteractionAttachments.isDeleted),
						eq(schema.chatInteractionAttachments.chatInteractionId, interactionId)
					)
				}
			}
		});
	}

	async getPromptsList() {
		return await this.repo.db.query.prompts.findMany({
			where: eq(schema.prompts.isDeleted, false),
			orderBy: [
				desc(schema.prompts.isDefault),
				desc(schema.prompts.isEnabled),
				desc(schema.prompts.updatedAt)
			]
		});
	}

	async getTool(id: string) {
		return await this.repo.db.query.tools.findFirst({
			where: eq(schema.tools.id, id)
		});
	}

	async getPrompt(id: string) {
		return await this.repo.db.query.prompts.findFirst({
			where: eq(schema.prompts.id, id)
		});
	}

	async upsertProfile(formData: typeof schema.profiles.$inferInsert) {
		await this.repo.db
			.insert(schema.profiles)
			.values({ ...formData })
			.onConflictDoUpdate({
				target: schema.profiles.id,
				set: { ...formData }
			});
	}

	async getProfile() {
		return this.repo.db.query.profiles.findFirst();
	}

	async resetDefaultPrompt() {
		await this.repo.db
			.update(schema.prompts)
			.set({ isDefault: false })
			.where(eq(schema.prompts.isDefault, true));
	}

	async upsertChatInteractionAttachment(
		attachment: typeof schema.chatInteractionAttachments.$inferInsert
	) {
		const result = await this.repo.db
			.insert(schema.chatInteractionAttachments)
			.values({
				...attachment,
				createdAt: undefined,
				updatedAt: undefined
			})
			.onConflictDoUpdate({
				target: schema.chatInteractionAttachments.id,
				set: {
					...attachment,
					updatedAt: new Date()
				}
			})
			.returning();
		return result.at(0);
	}

	async getStats() {
		const interactions = (
			await this.repo.db.select({ count: count() }).from(schema.chatInteractions)
		).at(0);
		const chats = (await this.repo.db.select({ count: count() }).from(schema.chats)).at(0);

		return {
			chats: chats?.count ?? 0,
			interactions: interactions?.count ?? 0
		};
	}
}
